import { Controller } from "@hotwired/stimulus";
import {
  isFailureOutcomePath,
  isSuccessOutcomePath,
  markAlreadyPaid,
  markPaymentFailed,
} from "../../../sessionCompletion";
import { debugLog, debugError } from "../../../utils";
import { storage } from "../../../storageconstants";

/** Static success / insufficient screens; fills Amount & Network from the checkout overview (same pattern as BlockchainProgress). */
export default class DirectDepositResultController extends Controller {
  static targets = [
    "totalPrice",
    "currency",
    "network",
    "txhash",
    "requiredAmount",
    "requiredToken",
    "receivedAmount",
    "receivedToken",
    "progressContainer",
    "progressBar",
    "progressStatusLabel",
    "progressDetectedLabel",
    "progressCompletedLabel",
    "finalContent",
    "orderSummary",
  ];

  connect() {
    // Shared controller also mounts on manual-check; only terminal outcomes set status.
    if (isSuccessOutcomePath()) {
      markAlreadyPaid();
    } else if (isFailureOutcomePath()) {
      markPaymentFailed();
    }
    this.progressTimeouts = [];
    this.hideLoadingScreen();
    this.fillOrderSummary();
    this.fillInsufficientAmounts();

    if (this.hasProgressBarTarget && this.hasFinalContentTarget && this.hasProgressContainerTarget) {
      this.startProgress();
    }
  }

  disconnect() {
    this.clearProgressTimers();
  }

  fillOrderSummary() {
    const checkoutPaymentMethod = document.getElementById("checkout_payment_method");
    const checkoutTotalAmount = document.getElementById("checkout_total_amount");

    if (!checkoutPaymentMethod || !checkoutTotalAmount) {
      debugError("DirectDepositResultController: checkout overview nodes missing");
      return;
    }

    const methodText = checkoutPaymentMethod.textContent || "";
    const amountText = checkoutTotalAmount.textContent || "";
    const network = methodText.split(" / ")[0]?.trim() || "";
    const currency = methodText.split(" / ")[1]?.trim() || "";
    const price = amountText.split(" ")[0]?.trim() || "";

    if (this.hasTotalPriceTarget) this.totalPriceTarget.textContent = price ? `${price} ` : "";
    if (this.hasCurrencyTarget) this.currencyTarget.textContent = currency;
    if (this.hasNetworkTarget) this.networkTarget.textContent = network;
  }

  fillInsufficientAmounts() {
    const raw = sessionStorage.getItem(storage.DIRECT_DEPOSIT_INSUFFICIENT_RESULT);
    if (!raw) return;

    try {
      const parsed = JSON.parse(raw);
      if (this.hasRequiredAmountTarget) this.requiredAmountTarget.textContent = parsed.requiredAmount || "";
      if (this.hasRequiredTokenTarget) this.requiredTokenTarget.textContent = parsed.requiredToken || "";
      if (this.hasReceivedAmountTarget) this.receivedAmountTarget.textContent = parsed.receivedAmount || "";
      if (this.hasReceivedTokenTarget) this.receivedTokenTarget.textContent = parsed.receivedToken || "";
    } catch (error) {
      debugError("DirectDepositResultController: invalid insufficient payload", error);
    } finally {
      sessionStorage.removeItem(storage.DIRECT_DEPOSIT_INSUFFICIENT_RESULT);
    }
  }

  showLoadingScreen() {
    this.dispatch("showLoadingScreen", { detail: { text: "Please Wait" } });
  }

  hideLoadingScreen() {
    this.dispatch("hideLoadingScreen");
  }

  startProgress() {
    this.updateProgressLabelsOpacity(0);
    this.setProgressStep(8, "Detected");

    this.progressTimeouts.push(
      setTimeout(() => this.setProgressStep(16, "Detected"), 400),
      setTimeout(() => this.setProgressStep(29, "Confirming"), 1200),
      // Intentional plateau to mimic network confirmation stalls.
      setTimeout(() => this.setProgressStep(33, "Confirming"), 2100),
      setTimeout(() => this.setProgressStep(48, "Confirming"), 3000),
      setTimeout(() => this.setProgressStep(66, "Confirming"), 3900),
      setTimeout(() => this.setProgressStep(78, "Confirming"), 4700),
      setTimeout(() => this.setProgressStep(91, "Finalizing"), 5300),
      setTimeout(() => this.completeProgress(), 5750),
    );
  }

  completeProgress() {
    const txHash = String(sessionStorage.getItem(storage.DIRECT_DEPOSIT_EXPLORER_URL));

    debugLog("txHash value in Results controller", txHash)
    this.populateTxHashLink(txHash);
    // Reveal TX Hash / Status rows only after the fade-out, so they don't snap in.
    this.pendingSuccessRows = txHash !== "";

    sessionStorage.removeItem(storage.DIRECT_DEPOSIT_EXPLORER_URL);

    this.setProgressStep(100, "Completed");

    // Match buy-app step transitions: fade out, then fade in (300ms each).
    this.progressTimeouts.push(
      setTimeout(() => {
        this.fadeOutProgress();
        this.progressTimeouts.push(
          setTimeout(() => {
            this.hideProgress();
            this.revealSuccessRows();
            this.showSuccessContent();
          }, 300),
        );
      }, 300),
    );
  }

  populateTxHashLink(rawExplorerURL) {
    if (!this.hasTxhashTarget) return;
    const linkElement = this.txhashTarget.querySelector("a");
    if (!linkElement) return;

    if (!rawExplorerURL) {
      linkElement.textContent = "";
      linkElement.removeAttribute("href");
      linkElement.removeAttribute("title");
      return;
    }

    try {
      const parsedURL = new URL(rawExplorerURL, window.location.origin);
      if (parsedURL.protocol !== "http:" && parsedURL.protocol !== "https:") {
        throw new Error("Unsupported explorer URL protocol");
      }

      // For hash-based explorers (e.g. tronscan.org/#/transaction/<hash>),
      // the identifier is in the fragment, not the pathname.
      const searchIn = parsedURL.hash
        ? parsedURL.hash.replace(/^#/, "")
        : parsedURL.pathname;
      const rawSegment = searchIn.split("/").filter(Boolean).pop() || parsedURL.toString();
      const decoded = decodeURIComponent(rawSegment);
      linkElement.href = parsedURL.toString();
      const shortDisplay =
        decoded.length > 8 ? `${decoded.slice(0, 4)}....${decoded.slice(-4)}` : decoded;
      linkElement.textContent = shortDisplay;
      linkElement.title = decoded;
    } catch (error) {
      debugError("DirectDepositResultController: invalid explorer URL", error);
      linkElement.textContent = "";
      linkElement.removeAttribute("href");
      linkElement.removeAttribute("title");
    }
  }

  setProgressStep(percentage, text) {
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${percentage}%`;
    }

    if (this.hasProgressStatusLabelTarget) {
      this.progressStatusLabelTarget.textContent = text;
      this.progressStatusLabelTarget.style.transition = "opacity .35s ease";
      this.progressStatusLabelTarget.style.opacity = percentage >= 100 ? "0.65" : "1";
    }

    this.updateProgressLabelsOpacity(percentage);
  }

  fadeOutProgress() {
    if (this.hasProgressContainerTarget) {
      this.progressContainerTarget.style.opacity = "0";
      this.progressContainerTarget.style.transform = "translateY(-20px)";
    }

    if (this.hasOrderSummaryTarget) {
      this.orderSummaryTarget.style.opacity = "0";
      this.orderSummaryTarget.style.transform = "translateY(-20px)";
    }
  }

  hideProgress() {
    if (!this.hasProgressContainerTarget) return;
    this.progressContainerTarget.classList.add("hidden");
  }

  revealSuccessRows() {
    if (!this.pendingSuccessRows) return;

    const successRow1 = document.getElementById("success_row_1");
    const successRow2 = document.getElementById("success_row_2");
    if (successRow1) {
      successRow1.classList.add("flex");
      successRow1.classList.remove("hidden");
    }
    if (successRow2) {
      successRow2.classList.add("flex");
      successRow2.classList.remove("hidden");
    }
  }

  showSuccessContent() {
    if (this.hasFinalContentTarget) {
      this.finalContentTarget.classList.remove("hidden");
      // Commit the opacity-0 / translateY(-20px) baseline before animating in
      // (same values as fadeInAnimationOnRender in app.js).
      void this.finalContentTarget.offsetHeight;
      this.finalContentTarget.style.opacity = "1";
      this.finalContentTarget.style.transform = "translateY(0px)";
    }

    if (this.hasOrderSummaryTarget) {
      void this.orderSummaryTarget.offsetHeight;
      this.orderSummaryTarget.style.opacity = "1";
      this.orderSummaryTarget.style.transform = "translateY(0px)";
    }
  }

  updateProgressLabelsOpacity(percentage) {
    if (this.hasProgressDetectedLabelTarget) {
      this.progressDetectedLabelTarget.style.transition = "opacity .35s ease";
      this.progressDetectedLabelTarget.style.opacity = percentage < 50 ? "1" : "0.35";
    }

    if (this.hasProgressCompletedLabelTarget) {
      this.progressCompletedLabelTarget.style.transition = "opacity .35s ease";
      this.progressCompletedLabelTarget.style.opacity = percentage >= 100 ? "1" : "0.35";
    }
  }

  clearProgressTimers() {
    if (!Array.isArray(this.progressTimeouts)) return;

    this.progressTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
    this.progressTimeouts = [];
  }
}
